import { ref } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';

export const useAccountStore = defineStore('account', () => {
  const loading = ref(false);
  const isLoggedIn = ref(false);
  const lastCheckedAt = ref(null);
  const details = ref(null);

  // Config: minimum minutes between checks
  const MINUTES_BETWEEN_CHECKS = 5; // <-- change as needed

  // Restore last check from localStorage (if any)
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('account.lastCheckedAt');
    if (saved) lastCheckedAt.value = saved;
    const savedLoggedIn = localStorage.getItem('account.isLoggedIn');
    if (savedLoggedIn !== null) isLoggedIn.value = savedLoggedIn === 'true';
  }

  const minutesSince = (iso) => {
    if (!iso) return Infinity;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return Infinity;
    return (Date.now() - then) / 1000 / 60;
  };

  /**
   * Bootstrap/auth check.
   * @param {Object} opts
   * @param {boolean} opts.force - set true to bypass throttle
   * @param {number} opts.minMinutes - override MINUTES_BETWEEN_CHECKS for this call
   */
  const checkStatus = async (opts = {}) => {
    const { force = false, minMinutes = MINUTES_BETWEEN_CHECKS } = opts;

    // Throttle: skip if checked recently (unless forced)
    if (!force && minutesSince(lastCheckedAt.value) < minMinutes) {
      return { skipped: true, reason: 'throttled', isLoggedIn: isLoggedIn.value };
    }

    // Don’t attempt if URL isn’t present
    const statusUrl = window?.Laravel?.shop?.account?.statusurl;
    if (!statusUrl) {
      // Update timestamp so we don't loop aggressively
      lastCheckedAt.value = new Date().toISOString();
      if (typeof window !== 'undefined') {
        localStorage.setItem('account.lastCheckedAt', lastCheckedAt.value);
      }
      return { skipped: true, reason: 'missing_status_url', isLoggedIn: isLoggedIn.value };
    }

    loading.value = true;
    try {
      // FIX: επιστρέφει error Unauthorized καθε φορά που γίνεται init το store
      const { data } = await axios.get(statusUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      isLoggedIn.value = !!data?.status;
      lastCheckedAt.value = new Date().toISOString();

      if (typeof window !== 'undefined') {
        localStorage.setItem('account.lastCheckedAt', lastCheckedAt.value);
        localStorage.setItem('account.isLoggedIn', String(isLoggedIn.value));
      }

      return { skipped: false, isLoggedIn: isLoggedIn.value };
    } catch (e) {
      isLoggedIn.value = false;
      lastCheckedAt.value = new Date().toISOString();
      if (typeof window !== 'undefined') {
        localStorage.setItem('account.lastCheckedAt', lastCheckedAt.value);
        localStorage.setItem('account.isLoggedIn', String(isLoggedIn.value));
      }
      return { skipped: false, error: true, isLoggedIn: isLoggedIn.value };
    } finally {
      loading.value = false;
    }
  };

  // TODO: replace async function with an axios promise
  const getDetails = async (url = window.Laravel.shop.account.bootstrap) => {
    try {
      const response = await axios.get(url, { headers: { Accept: 'application/json' } });

      if (response.status === 200) {
        details.value = response.data.data.user;
      }

      return response;
    } catch (error) {
      return error;
    }
  }

  // call once on load, but let the throttle prevent spam
  checkStatus({ force: true }).catch(() => { });

  return {
    loading,
    isLoggedIn,
    lastCheckedAt,
    details,
    checkStatus,
    getDetails,
    MINUTES_BETWEEN_CHECKS, // export so you can tweak at runtime if needed
  };
});
